// The one large control the hub is built around (TX-US-0028): press, talk, press again.
//
// What it shows follows the recording's life: the button to start; the elapsed time and a ring
// that moves with the voice while recording; "Saving" while the take goes to storage; the
// recording itself once it is there; and, when a take did not get through, the take still on the
// phone with a way to send it again.

import { useRef } from 'react';

import Icon from '../ui/Icon.jsx';
import RecordingPlayer from './RecordingPlayer.jsx';
import { MAX_RECORDING_MS, formatElapsed } from './recorder.js';
import { timeLabel } from './localDay.js';
import { SENDING } from './useHubSession.js';
import useRecorder from './useRecorder.js';

export default function RecordControl({ hub }) {
  const ring = useRef(null);
  const recorder = useRecorder({ onTake: hub.sendRecording, levelTarget: ring });
  const { draft, sending, unsent } = hub;
  const saved = draft?.recording ?? null;

  if (!recorder.supported) {
    return (
      <div className="hub-record">
        <p className="hub-record-note">
          This browser cannot record. Type your update, or attach a voice note instead.
        </p>
      </div>
    );
  }

  const busy = sending === SENDING.sending;
  // A take on the phone that is not the one being sent right now: a failed upload, or one found
  // when the hub was opened.
  const waiting = unsent && !busy && !recorder.recording;

  return (
    <div className={`hub-record${recorder.recording ? ' recording' : ''}`}>
      {recorder.recording && (
        <p className="hub-elapsed" role="timer" aria-live="off">
          {formatElapsed(recorder.elapsedMs)}
          <small> / {formatElapsed(MAX_RECORDING_MS)}</small>
        </p>
      )}

      <button
        ref={ring}
        type="button"
        className="hub-record-button"
        onClick={recorder.recording ? recorder.stop : recorder.start}
        disabled={busy}
        aria-pressed={recorder.recording}
        aria-label={recorder.recording ? 'Stop recording' : 'Record'}
      >
        <Icon name={recorder.recording ? 'stop' : 'mic'} />
      </button>

      <p className="hub-record-label" aria-live="polite">
        {recorder.recording && 'Recording — tap to stop'}
        {!recorder.recording && busy && 'Saving your recording…'}
        {!recorder.recording && !busy && (saved ? 'Tap to record again' : 'Tap to record')}
      </p>

      {recorder.error && <p className="error">{recorder.error}</p>}

      {waiting && (
        <div className="hub-unsent" role="status">
          <p>
            A recording from {timeLabel(unsent.recordedAt)}
            {' '}({formatElapsed(unsent.durationSeconds * 1000)}) is on this phone and has not
            been uploaded.
          </p>
          <div className="hub-unsent-actions">
            <button type="button" className="primary" onClick={hub.sendUnsent}>Upload it</button>
            <button type="button" onClick={hub.dropUnsent}>Delete it</button>
          </div>
        </div>
      )}

      {saved && !recorder.recording && !busy && (
        <div className="hub-recorded">
          <RecordingPlayer key={saved.size_bytes} sessionId={draft.session_id} recording={saved} />
          <button type="button" className="icon-button quiet" onClick={hub.removeRecording}
                  aria-label="Remove recording" title="Remove recording">
            <Icon name="trash" />
          </button>
        </div>
      )}
    </div>
  );
}
